import { useInput } from 'react-admin';
import { useFormState } from 'react-final-form';
import {
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  MenuItem,
  Select,
  TextField,
} from '@material-ui/core';
import React, { useMemo, useState } from 'react';
import { Button } from 'react-admin';
import { fetchCdpJson } from '../../../../../helpers/cdp';
import { findCurrentState } from '../state';
import WeeklyPlanningDialog from './WeeklyPlanningDialog';
import ContractualPlanningSlotsGrid from './ContractualPlanningSlotsGrid';
import { deserializePlanning, serializePlanning } from './planningUtils';

// eslint-disable-next-line react/prop-types
const ContractMetadataForm = ({ source, record }) => {
  const {
    input: { value, onChange },
  } = useInput({ source });

  const { values, errors, submitFailed } = useFormState();

  const [planningOpen, setPlanningOpen] = useState(false);
  const serializedPlanning = value?.contractualPlanning || '';
  const planningObj = useMemo(() => {
    return deserializePlanning(serializedPlanning);
  }, [serializedPlanning]);

  const summary = useMemo(() => {
    const days = Object.keys(planningObj || {});
    const countDays = days.filter((d) => {
      return (planningObj?.[d] || []).length > 0;
    }).length;
    return countDays > 0
      ? `${countDays} jour(s) planifié(s)`
      : 'Aucun horaire défini';
  }, [planningObj]);

  const handleFieldChange = (field, fieldValue) => {
    onChange({
      ...value,
      [field]: fieldValue,
    });
  };

  const contractType = values.contract_type;

  const { metadataFields } = findCurrentState(contractType, value?.type);

  const displayField = (field) => {
    return metadataFields?.includes(field);
  };

  const normalizeTime = (t) => {
    if (!t) return '';
    const [h = '0', m = '0'] = String(t).split(':');
    const hh = String(h).padStart(2, '0');
    const mm = String(m).padStart(2, '0');
    return `${hh}:${mm}`;
  };

  const dayKeyMap = {
    monday: 'Mon',
    tuesday: 'Tue',
    wednesday: 'Wed',
    thursday: 'Thu',
    friday: 'Fri',
    saturday: 'Sat',
    sunday: 'Sun',
  };

  const importFromReference = async () => {
    const cleanerId = values?.cleaner_id || record?.cleaner_id;
    const contractId = record?.id;
    const startDate = values?.start_date || record?.start_date;
    const endDate = values?.end_date || record?.end_date;

    if (!contractId || !startDate) {
      window.alert(
        'Paramètres manquants pour importer le planning de référence (contrat ou date de début).',
      );
      return;
    }

    try {
      const payload = await fetchCdpJson('/cdp/generate-reference-planning', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ cleanerId, contractId, startDate, endDate }),
      });

      if (!payload) {
        window.alert('Réponse vide du service de planning de référence.');
        return;
      }

      let parsed;
      try {
        parsed = JSON.parse(String(payload).substring(8));
      } catch (e) {
        console.error('Error parsing reference planning payload', e, payload);
        window.alert(
          "Impossible d'interpréter le planning de référence renvoyé par le serveur.",
        );
        return;
      }

      const result = {};
      Object.keys(parsed || {}).forEach((day) => {
        const key = dayKeyMap[day];
        const slots = Array.isArray(parsed[day]) ? parsed[day] : [];
        const mapped = slots
          .filter((s) => {
            return s && s.start && s.end;
          })
          .map((s) => {
            return { start: normalizeTime(s.start), end: normalizeTime(s.end) };
          })
          .sort((a, b) => {
            return a.start.localeCompare(b.start);
          });
        if (key && mapped.length) {
          result[key] = mapped;
        }
      });

      handleFieldChange('contractualPlanning', serializePlanning(result));
    } catch (err) {
      console.error('Error while importing reference planning', err);
      window.alert(
        err.message ||
          'Erreur lors de la récupération du planning de référence.',
      );
    }
  };

  return (
    <FormControl component="fieldset" style={{ width: '100%' }}>
      <FormGroup>
        {displayField('hasProbationPeriod') && (
          <FormControlLabel
            style={{ marginTop: 20 }}
            control={
              <Checkbox
                checked={value?.hasProbationPeriod}
                onChange={(e) => {
                  const isChecked = e.target.checked;

                  onChange({
                    ...value,
                    hasProbationPeriod: isChecked,
                    probationPeriodDurationQuantity: isChecked
                      ? value?.probationPeriodDurationQuantity
                      : '',
                    probationPeriodDurationUnit: isChecked
                      ? value?.probationPeriodDurationUnit
                      : '',
                  });
                }}
              />
            }
            label="Inclure une période d'essai"
          />
        )}
        {value?.hasProbationPeriod && (
          <div
            style={{
              display: 'flex',
              marginTop: 20,
              flexDirection: 'row',
              gap: 5,
            }}
          >
            <TextField
              placeholder="Nombre de jours / mois de période d'essai"
              style={{ width: 100 }}
              type="number"
              value={value?.probationPeriodDurationQuantity || ''}
              onChange={(e) => {
                return handleFieldChange(
                  'probationPeriodDurationQuantity',
                  e.target.value,
                );
              }}
            />
            <Select
              displayEmpty
              labelId="probation-period-unit-label"
              value={value?.probationPeriodDurationUnit || ''}
              onChange={(e) => {
                return handleFieldChange(
                  'probationPeriodDurationUnit',
                  e.target.value,
                );
              }}
            >
              <MenuItem disabled value="">
                <em>Jours / Mois</em>
              </MenuItem>
              <MenuItem value="DAY">Jours</MenuItem>
              <MenuItem value="MONTH">Mois</MenuItem>
            </Select>
          </div>
        )}
        {displayField('isAnnexeSeven') && (
          <FormControlLabel
            control={
              <Checkbox
                checked={value?.isAnnexeSeven || false}
                onChange={(e) => {
                  return handleFieldChange('isAnnexeSeven', e.target.checked);
                }}
              />
            }
            label="Article 7 ?"
          />
        )}
        {displayField('hasFewWeeklyHoursWaiver') && (
          <FormControlLabel
            control={
              <Checkbox
                checked={value?.hasFewWeeklyHoursWaiver || false}
                onChange={(e) => {
                  return handleFieldChange(
                    'hasFewWeeklyHoursWaiver',
                    e.target.checked,
                  );
                }}
              />
            }
            label="Nécessite une dérogation pour moins de 16h ? "
          />
        )}
        {displayField('contractualPlanning') && (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
              marginTop: 12,
            }}
          >
            {submitFailed && errors?.metadata?.contractualPlanning && (
              <div
                style={{
                  color: '#d32f2f',
                  fontSize: 13,
                  textAlign: 'center',
                }}
              >
                {errors.metadata.contractualPlanning}
              </div>
            )}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                alignContent: 'center',
                flexDirection: 'column',
                marginLeft: 'auto',
                marginRight: 'auto',
                gap: 12,
                width: '100%',
              }}
            >
              <ContractualPlanningSlotsGrid
                serialized={value?.contractualPlanning}
                totalWorkTime={values?.contract_worked_time}
              />
              <Button
                label="Configurer le planning"
                variant="contained"
                onClick={() => {
                  setPlanningOpen(true);
                }}
              />
              <Button
                label="Importer depuis le planning de référence"
                onClick={importFromReference}
              />
            </div>
            <WeeklyPlanningDialog
              open={planningOpen}
              onClose={() => {
                setPlanningOpen(false);
              }}
              initialSerialized={value?.contractualPlanning || ''}
              totalWorkTime={values?.contract_worked_time}
              onSave={(serialized) => {
                setPlanningOpen(false);
                handleFieldChange('contractualPlanning', serialized);
              }}
            />
          </div>
        )}
      </FormGroup>
    </FormControl>
  );
};

export default ContractMetadataForm;
